import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Ship } from '../../services/shipService';
import { 
  ChevronUp, ChevronDown, Zap, Shield, Gauge, Box, 
  Users, DollarSign, Rocket
} from 'lucide-react';
import styles from './ShipListView.module.css';

export type SortField = 'name' | 'manufacturer' | 'size' | 'maxSpeed' | 'cargoCapacity' | 
                        'crewMax' | 'priceAUEC' | 'role' | 'hullHealth';

interface ShipListViewProps {
  ships: Ship[];
  sortField: SortField;
  sortDirection: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  onFilterByManufacturer?: (manufacturer: string) => void;
  onFilterByRole?: (role: string) => void;
  onFilterBySize?: (size: string) => void;
}

const ShipListView: React.FC<ShipListViewProps> = ({ 
  ships, 
  sortField, 
  sortDirection, 
  onSort,
  onFilterByManufacturer,
  onFilterByRole,
  onFilterBySize
}) => {
  const navigate = useNavigate();

  const handleRowClick = (slug: string) => {
    navigate(`/ship-database/${slug}`);
  };

  const formatPrice = (price: number | null) => {
    if (!price) return '—';
    if (price >= 1000000) return (price / 1000000).toFixed(1) + 'M';
    if (price >= 1000) return (price / 1000).toFixed(0) + 'K';
    return price.toLocaleString();
  };

  const formatSpeed = (speed: number | null) => {
    if (!speed) return '—';
    return Math.round(speed) + ' m/s';
  };

  const formatCargo = (cargo: number | null) => {
    if (cargo === null || cargo === undefined) return '—';
    return cargo + ' SCU';
  };

  const formatCrew = (min: number | null, max: number | null) => {
    if (!min && !max) return '—';
    if (min === max) return String(max || 1);
    return `${min || 1}-${max || 1}`;
  };

  const formatHealth = (health: number | null) => {
    if (!health) return '—';
    if (health >= 1000000) return (health / 1000000).toFixed(1) + 'M';
    if (health >= 1000) return (health / 1000).toFixed(0) + 'K';
    return health.toLocaleString();
  };

  const getSizeOrder = (size: string | null): number => {
    const order: Record<string, number> = {
      'Vehicle': 1, 'Snub': 2, 'Small': 3, 'Medium': 4, 
      'Large': 5, 'Capital': 6
    };
    return order[size || ''] || 0;
  };

  const getStatusClass = (status: string | null) => {
    if (!status) return '';
    const statusLower = status.toLowerCase().replace(/\s+/g, '');
    if (statusLower.includes('flightready')) return styles.flightReady;
    if (statusLower.includes('production')) return styles.inProduction;
    if (statusLower.includes('concept')) return styles.inConcept;
    return '';
  };

  const SortHeader: React.FC<{ field: SortField; label: string; icon?: React.ReactNode }> = ({ 
    field, label, icon 
  }) => (
    <th 
      className={`${styles.sortableHeader} ${sortField === field ? styles.active : ''}`}
      onClick={() => onSort(field)}
    >
      <div className={styles.headerContent}>
        {icon}
        <span>{label}</span>
        <div className={styles.sortIndicator}>
          {sortField === field ? (
            sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
          ) : (
            <div className={styles.sortPlaceholder} />
          )}
        </div>
      </div>
    </th>
  );

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.imageHeader}></th>
            <SortHeader field="name" label="Ship" icon={<Rocket size={14} />} />
            <SortHeader field="manufacturer" label="Manufacturer" />
            <SortHeader field="role" label="Role" />
            <SortHeader field="size" label="Size" />
            <SortHeader field="maxSpeed" label="Speed" icon={<Gauge size={14} />} />
            <SortHeader field="cargoCapacity" label="Cargo" icon={<Box size={14} />} />
            <SortHeader field="crewMax" label="Crew" icon={<Users size={14} />} />
            <SortHeader field="hullHealth" label="Hull HP" icon={<Shield size={14} />} />
            <SortHeader field="priceAUEC" label="Price (aUEC)" icon={<DollarSign size={14} />} />
            <th className={styles.statusHeader}>Status</th>
          </tr>
        </thead>
        <tbody>
          {ships.map((ship) => (
            <tr 
              key={ship.id} 
              className={styles.row}
              onClick={() => handleRowClick(ship.slug)}
            >
              <td className={styles.imageCell}>
                {ship.thumbnailUrl ? (
                  <img 
                    src={ship.thumbnailUrl} 
                    alt={ship.name} 
                    className={styles.thumbnail}
                  />
                ) : (
                  <img src="/images/ship-art-placeholder.png" alt="Artwork coming soon" className={styles.thumbnail} />
                )}
              </td>
              <td className={styles.nameCell}>
                <div className={styles.shipName}>{ship.name}</div>
                {(ship.hasHardpoints || (ship.hardpoints && ship.hardpoints.length > 0)) && (
                  <div className={styles.enhancedBadge}>
                    <Zap size={10} />
                    <span>P4K</span>
                  </div>
                )}
              </td>
              <td className={styles.manufacturerCell}>
                {ship.manufacturer ? (
                  <span 
                    className={styles.clickableFilter}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFilterByManufacturer?.(ship.manufacturer!);
                    }}
                    title={`Filter by ${ship.manufacturer}`}
                  >
                    {ship.manufacturer}
                  </span>
                ) : '—'}
              </td>
              <td className={styles.roleCell}>
                {ship.role ? (
                  <span 
                    className={`${styles.roleBadge} ${styles.clickableFilter}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFilterByRole?.(ship.role!);
                    }}
                    title={`Filter by ${ship.role}`}
                  >
                    {ship.role}
                  </span>
                ) : (
                  <span className={styles.roleBadge}>Multi-Role</span>
                )}
              </td>
              <td className={styles.sizeCell}>
                {ship.size ? (
                  <span 
                    className={`${styles.sizeBadge} ${styles[`size${ship.size}`]} ${styles.clickableFilter}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFilterBySize?.(ship.size!);
                    }}
                    title={`Filter by ${ship.size}`}
                  >
                    {ship.size}
                  </span>
                ) : (
                  <span className={styles.sizeBadge}>—</span>
                )}
              </td>
              <td className={styles.speedCell}>
                {formatSpeed(ship.maxSpeed)}
              </td>
              <td className={styles.cargoCell}>
                {formatCargo(ship.cargoCapacity)}
              </td>
              <td className={styles.crewCell}>
                {formatCrew(ship.crewMin, ship.crewMax)}
              </td>
              <td className={styles.healthCell}>
                {formatHealth(ship.hullHealth)}
              </td>
              <td className={styles.priceCell}>
                {formatPrice(ship.priceAUEC)}
              </td>
              <td className={styles.statusCell}>
                <span className={`${styles.statusBadge} ${getStatusClass(ship.productionStatus)}`}>
                  {ship.productionStatus || '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ShipListView;
